import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Head from 'next/head';
import Cookies from 'js-cookie';
import initializeFreshbots from '@utils/freshbotsInit';
import { useRouter } from 'next/router';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { onChatOpen } from '@actions/helpCenter';
import { fetchMeData } from '@api/fetchers/helpCenter';
import setupInitialProps from '@utils/setupInitialProps';
import { captureException } from '@utils/sentry';
import { SupportPageAuthError } from '@utils/customErrors';
import { getAccessToken, getIsLoggedIn, getMe } from '@selectors/auth';
import { fetchMyInfo } from '@actions/auth';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useGeoLocation from '@api/hooks/useGeoLocation';
import goalCookies, { setCookie } from '@utils/cookies';
import useFetch from '@hooks/useFetch';

const SupportPage = ({ jwtToken }) => {
  const router = useRouter();
  const dispatch = useDispatchToStore();

  const me = useSelector(getMe);
  const goalUid =
    me.selectedGoal ||
    goalCookies.readCookie('selectedGoalUid') ||
    goalCookies.readCookie('selectedGoal') ||
    'NA';
  const accessToken = useSelector(getAccessToken);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const selectedGoal = useSelectedGoalUID();
  const { geoLocationData: { countryCode } = {} } = useGeoLocation();

  const { user_role, inside_app } = router.query;
  const { data: selfData = {} } = useFetch(
    `v1/topology/users/${goalUid}/self/`
  );

  const initializeBot = async (fetchUserDataBeforeInitializing = false) => {
    if (fetchUserDataBeforeInitializing)
      await dispatch(fetchMyInfo(router.pathname));
    const roles = { 1: 'learner', 2: 'educator' };
    dispatch(onChatOpen(roles[user_role], me.jwtToken || ''));
    const response = await fetchMeData(roles[user_role]);
    initializeFreshbots(
      '/support',
      {
        me,
        isLoggedIn,
        selectedGoal,
        accessToken: accessToken || Cookies.get('accessToken'),
        selfData
      },
      response,
      countryCode
    );
  };

  useEffect(() => {
    // Set cookie for webview explicitly
    if (inside_app && jwtToken) setCookie('jwtToken', jwtToken, 3024000, true);

    if (inside_app) {
      const cookies = Cookies.get();
      if (!cookies.accessToken)
        captureException(
          new SupportPageAuthError(
            'Access token missing from support page for inside app call'
          )
        );
    }
    // If for some reason, user is marked as not logged in but the cookie exists. We still can fetch the user data till the background processes happen to update the user's login status.
    const fetchUserDataBeforeInitializing =
      !isLoggedIn && (accessToken || Cookies.get('accessToken'));
    // If the user is logged in or the access token is present, initialize the bot.
    if (isLoggedIn || fetchUserDataBeforeInitializing)
      initializeBot(fetchUserDataBeforeInitializing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, inside_app, accessToken, isLoggedIn]);

  return (
    <>
      <Head>
        <link
          href="https://cdn.in-freshbots.ai/assets/share/css/chat-combined.min.css?v=0.26"
          rel="stylesheet"
        />
      </Head>
    </>
  );
};

SupportPage.pageInfo = {
  name: 'Unacademy Support',
  isMWebSupported: true,
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true
  }
};

SupportPage.getInitialProps = setupInitialProps(
  ({ store }) => ({ jwtToken: store.getState().auth.me?.jwtToken || null }),
  SupportPage.pageInfo
);

export default SupportPage;
SupportPage.Layout = ({ children }) => children;
SupportPage.Wrapper = PageReduxProvider;
